import type { EntryKind, LogEntry } from "../core/eventLog";
import { truncatePreview } from "./format";

/** Filter criteria applied to the timeline. Every field is optional / additive (AND). */
export type TimelineFilters = {
  moduleId?: string;
  kind?: EntryKind;
  contactId?: string;
  text?: string;
};

const EMPTY: TimelineFilters = {};

/**
 * Applies {@link TimelineFilters} to a list of entries. Pure and side-effect free so it is unit
 * testable without mounting the Timeline panel. `text` matches case-insensitively against
 * moduleId, name, contactId, and a stringified preview of the payload.
 */
export function filterEntries(
  entries: readonly LogEntry[],
  filters: TimelineFilters = EMPTY,
): LogEntry[] {
  const text = filters.text?.trim().toLowerCase();
  return entries.filter((e) => {
    if (filters.moduleId && e.moduleId !== filters.moduleId) return false;
    if (filters.kind && e.kind !== filters.kind) return false;
    if (filters.contactId && e.contactId !== filters.contactId) return false;
    if (text) {
      const haystack = [
        e.moduleId,
        e.name,
        e.contactId ?? "",
        truncatePreview(e.payload, 500),
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(text)) return false;
    }
    return true;
  });
}

/** Distinct module ids present in the log, for populating the module filter's options. */
export function distinctModuleIds(entries: readonly LogEntry[]): string[] {
  return [...new Set(entries.map((e) => e.moduleId))].sort();
}

/** Distinct contact ids present in the log, for populating the contact filter's options. */
export function distinctContactIds(entries: readonly LogEntry[]): string[] {
  return [
    ...new Set(
      entries
        .map((e) => e.contactId)
        .filter((c): c is string => typeof c === "string"),
    ),
  ].sort();
}
